import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase, storage } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import type { OrderStatus } from '../../types';
import { formatDate } from '../../lib/utils';

type OrderFile = {
  id: string;
  file_name: string;
  file_path: string;
  file_size?: number;
  sender_role?: string;
  created_at?: string;
};

type FranchiseOrder = {
  id: string;
  order_number?: string;
  status: OrderStatus;
  created_at: string;
  vehicle_plate?: string;
  model?: string;
  notes?: string;
  order_files?: OrderFile[];
};

export default function FranchiseOrders() {
  const { user, franchisee, loading: authLoading } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [orders, setOrders] = useState<FranchiseOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [visibleCount, setVisibleCount] = useState(10);
  const [searchParams, setSearchParams] = useSearchParams();

  const colors = {
    bgCard: isDark ? '#111' : '#ffffff',
    bgCardHover: isDark ? '#1a1a1a' : '#fafafa',
    borderCard: isDark ? '#1e1e1e' : '#e0e0e0',
    textPrimary: isDark ? '#fff' : '#1a1a1a',
    textSecondary: isDark ? '#888' : '#666',
    textMuted: isDark ? '#555' : '#999',
    accent: '#c8c8c8',
    statusAmber: isDark ? '#777777' : '#f0f0f0',
    statusBlue: isDark ? '#444333' : '#f0f0f0',
    statusGreen: isDark ? '#444333' : '#f0f0f0',
    statusRed: isDark ? '#444333' : '#f0f0f0',
    badgeFile: isDark ? '#1e1e1e' : '#f0f0f0',
    badgeFileColor: isDark ? '#c8c8c8' : '#b0b0b0',
  };

  useEffect(() => {
    if (authLoading) return;
    if (user?.id) {
      loadOrders();
    } else {
      setLoadingOrders(false);
    }
  }, [user?.id, franchisee?.id, authLoading]);

  useEffect(() => {
    const orderIdFromQuery = searchParams.get('orderId');
    if (!orderIdFromQuery) return;

    const matchedOrder = orders.find((order) => order.id === orderIdFromQuery);
    if (matchedOrder) {
      setExpandedOrderId(orderIdFromQuery);
      return;
    }

    if (!loadingOrders) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('orderId');
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParams, setSearchParams, orders, loadingOrders]);

  async function loadOrders() {
    if (!user?.id) return;

    setLoadingOrders(true);
    setLoadError('');

    try {
      const ownershipFilter = franchisee?.id
        ? `franchisee_id.eq.${franchisee.id},created_by.eq.${user.id}`
        : `created_by.eq.${user.id}`;

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('id, order_number, status, created_at, vehicle_plate, model, notes')
        .or(ownershipFilter)
        .order('created_at', { ascending: false });

      if (ordersError) throw new Error(`Erro ao carregar pedidos: ${ordersError.message}`);

      const orderIds = (ordersData || []).map((o) => o.id);
      const filesByOrder = new Map<string, OrderFile[]>();

      if (orderIds.length > 0) {
        const { data: filesData, error: filesError } = await supabase
          .from('order_files')
          .select('id, order_id, file_name, file_path, file_size, sender_role, created_at')
          .in('order_id', orderIds)
          .order('created_at', { ascending: false });

        if (filesError) throw new Error(`Erro ao carregar arquivos: ${filesError.message}`);

        for (const file of filesData || []) {
          if (!filesByOrder.has(file.order_id)) filesByOrder.set(file.order_id, []);
          filesByOrder.get(file.order_id)!.push({
            id: file.id,
            file_name: file.file_name,
            file_path: file.file_path,
            file_size: file.file_size,
            sender_role: file.sender_role,
            created_at: file.created_at,
          });
        }
      }

      setOrders(
        (ordersData || []).map((order) => ({
          ...order,
          order_files: filesByOrder.get(order.id) || [],
        })) as FranchiseOrder[]
      );
    } catch (err: unknown) {
      setLoadError((err as Error).message || 'Erro desconhecido ao carregar pedidos.');
    } finally {
      setLoadingOrders(false);
    }
  }

  async function downloadFile(filePath: string) {
    try {
      const url = await storage.getSignedUrl(filePath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setLoadError('Não foi possível abrir o arquivo. Tente novamente.');
    }
  }

  const getStatusText = (status: OrderStatus) => {
    if (status === 'concluido') return 'Concluído';
    if (status === 'solicitado') return 'Recebido';
    if (status === 'cancelado') return 'Cancelado';
    if (status === 'enviado') return 'Enviado';
    return 'Em andamento';
  };

  const getStatusColor = (status: OrderStatus) => {
    const colorMap: Record<OrderStatus, string> = {
      solicitado: colors.statusAmber,
      em_producao: colors.statusBlue,
      enviado: colors.statusBlue,
      concluido: colors.statusGreen,
      cancelado: colors.statusRed,
    };
    return colorMap[status] || colors.bgCard;
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });
  const visibleOrders = sortedOrders.slice(0, visibleCount);

  useEffect(() => setVisibleCount(10), [sortOrder]);

  if (!franchisee && !authLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: colors.statusRed }}>
        <h2>Erro de acesso</h2>
        <p>Você não tem permissão para acessar esta página.</p>
      </div>
    );
  }

  if (authLoading || loadingOrders) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: colors.textSecondary }}>
        Carregando pedidos...
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px' }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: colors.textPrimary }}>Meus Pedidos</h1>
          <p style={{ margin: '4px 0 0', color: colors.textSecondary, fontSize: 13 }}>
            Histórico dos pedidos enviados, com arquivos e status atualizados pelo administrador
          </p>
        </div>
        <Link
          to="/orders/new"
          style={{
            padding: '10px 24px',
            background: '#c8c8c8',
            borderRadius: 8,
            color: '#000',
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          Ir para Novo Pedido
        </Link>
      </div>

      {loadError && (
        <div style={{ marginBottom: 16, padding: '14px 16px', background: '#3b1f1f', color: '#f87171', borderRadius: 12, fontSize: 13 }}>
          {loadError}
        </div>
      )}

      <div style={{ background: colors.bgCard, border: `1px solid ${colors.borderCard}`, borderRadius: 12, overflow: 'hidden' }}>
        {/* Toolbar */}
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.borderCard}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <span style={{ fontSize: 13, color: colors.textSecondary }}>
            Total: {orders.length} pedido(s)
          </span>
          <button
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            style={{ background: 'transparent', border: 'none', color: colors.textPrimary, cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            Ordenar por data {sortOrder === 'desc' ? '↓' : '↑'}
          </button>
        </div>

        {sortedOrders.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: colors.textSecondary }}>
            Nenhum pedido encontrado. Use Novo Pedido para enviar sua primeira solicitação.
          </div>
        ) : (
          <>
          {visibleOrders.map((order) => {
            const remapFiles = (order.order_files || []).filter((f) => f.sender_role === 'admin');
            const originalFiles = (order.order_files || []).filter((f) => f.sender_role !== 'admin');
            const totalFiles = order.order_files?.length || 0;

            return (
              <div key={order.id} style={{ borderBottom: `1px solid ${colors.borderCard}`, transition: 'all 0.2s' }}>
                {/* Linha clicável */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '140px 1fr 140px 130px',
                    alignItems: 'center',
                    padding: '14px 20px',
                    cursor: 'pointer',
                    background: expandedOrderId === order.id ? colors.bgCardHover : 'transparent',
                    gap: 10,
                  }}
                  onClick={() => {
                    const nextExpandedOrderId = expandedOrderId === order.id ? null : order.id;
                    setExpandedOrderId(nextExpandedOrderId);
                    const nextParams = new URLSearchParams(searchParams);
                    if (nextExpandedOrderId) {
                      nextParams.set('orderId', nextExpandedOrderId);
                    } else {
                      nextParams.delete('orderId');
                    }
                    setSearchParams(nextParams, { replace: true });
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 600, color: colors.textPrimary, fontSize: 13 }}>
                      {order.order_number || order.id.slice(0, 8)}
                    </span>
                  </div>

                  <div>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 10px',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      background: getStatusColor(order.status),
                      color: isDark ? '#fff' : '#1a1a1a',
                    }}>
                      {getStatusText(order.status)}
                    </span>
                  </div>

                  <div style={{ fontSize: 12, color: colors.textSecondary }}>
                    {formatDate(order.created_at)}
                  </div>

                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {totalFiles > 0 && (
                      <span style={{
                        fontSize: 11,
                        color: colors.badgeFileColor,
                        background: colors.badgeFile,
                        padding: '3px 8px',
                        borderRadius: 4,
                      }}>
                        {totalFiles} arquivo(s)
                      </span>
                    )}
                    {remapFiles.length > 0 && (
                      <span style={{
                        fontSize: 11,
                        color: '#4ade80',
                        background: isDark ? '#0a2e16' : '#f0fdf4',
                        border: '1px solid #4ade80',
                        padding: '3px 8px',
                        borderRadius: 4,
                        fontWeight: 700,
                      }}>
                        {remapFiles.length} remap
                      </span>
                    )}
                  </div>
                </div>

                {/* Detalhes expandidos */}
                {expandedOrderId === order.id && (
                  <div style={{ padding: '16px 20px 24px', background: colors.bgCardHover, borderTop: `1px solid ${colors.borderCard}` }}>

                    {/* Dados do pedido */}
                    {(order.vehicle_plate || order.model || order.notes) && (
                      <div style={{ marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                        {order.vehicle_plate && (
                          <div>
                            <div style={{ fontSize: 10, color: colors.textMuted, fontWeight: 700, letterSpacing: 0.5 }}>PLACA</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary, marginTop: 3 }}>{order.vehicle_plate}</div>
                          </div>
                        )}
                        {order.model && (
                          <div>
                            <div style={{ fontSize: 10, color: colors.textMuted, fontWeight: 700, letterSpacing: 0.5 }}>MODELO</div>
                            <div style={{ fontSize: 14, color: colors.textPrimary, marginTop: 3 }}>{order.model}</div>
                          </div>
                        )}
                        {order.notes && (
                          <div style={{ flex: 1, minWidth: 200 }}>
                            <div style={{ fontSize: 10, color: colors.textMuted, fontWeight: 700, letterSpacing: 0.5 }}>OBSERVAÇÕES</div>
                            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 3 }}>{order.notes}</div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Arquivos */}
                    {totalFiles === 0 ? (
                      <div style={{ fontSize: 12, color: colors.textMuted, fontStyle: 'italic' }}>
                        Nenhum arquivo neste pedido ainda.
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

                        {/* Arquivos REMAP (enviados pelo admin) */}
                        {remapFiles.length > 0 && (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: '#4ade80', letterSpacing: 0.5, marginBottom: 8 }}>
                              📤 ARQUIVOS REMAP ({remapFiles.length})
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {remapFiles.map((file) => (
                                <div
                                  key={file.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 10,
                                    padding: '10px 14px',
                                    borderRadius: 8,
                                    border: '1px solid #4ade80',
                                    background: isDark ? '#0a2e16' : '#f0fdf4',
                                  }}
                                >
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, wordBreak: 'break-word' }}>
                                      {file.file_name}
                                    </div>
                                    <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                                      {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : ''}
                                      {file.file_size && file.created_at ? ' · ' : ''}
                                      {file.created_at ? formatDate(file.created_at) : ''}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => downloadFile(file.file_path)}
                                    style={{
                                      flexShrink: 0,
                                      padding: '7px 16px',
                                      borderRadius: 8,
                                      border: 'none',
                                      background: '#4ade80',
                                      color: '#052e16',
                                      fontSize: 12,
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    ⬇ Baixar
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Arquivos ORIGINAIS (enviados pelo franqueado) */}
                        {originalFiles.length > 0 && (
                          <div>
                            <div style={{ fontSize: 10, fontWeight: 700, color: colors.textSecondary, letterSpacing: 0.5, marginBottom: 8 }}>
                              📥 ARQUIVOS ORIGINAIS ({originalFiles.length})
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {originalFiles.map((file) => (
                                <div
                                  key={file.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 10,
                                    padding: '10px 14px',
                                    borderRadius: 8,
                                    border: `1px solid ${colors.borderCard}`,
                                    background: isDark ? '#161616' : '#fafafa',
                                  }}
                                >
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary, wordBreak: 'break-word' }}>
                                      {file.file_name}
                                    </div>
                                    <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
                                      {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : ''}
                                      {file.file_size && file.created_at ? ' · ' : ''}
                                      {file.created_at ? formatDate(file.created_at) : ''}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => downloadFile(file.file_path)}
                                    style={{
                                      flexShrink: 0,
                                      padding: '7px 16px',
                                      borderRadius: 8,
                                      border: 'none',
                                      background: isDark ? '#2a2a2a' : '#e5e7eb',
                                      color: colors.textPrimary,
                                      fontSize: 12,
                                      fontWeight: 700,
                                      cursor: 'pointer',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    ⬇ Baixar
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {visibleCount < sortedOrders.length && (
            <div style={{ padding: 16, textAlign: 'center', borderTop: `1px solid ${colors.borderCard}` }}>
              <button type="button" onClick={() => setVisibleCount((count) => count + 10)}
                style={{ padding: '8px 18px', borderRadius: 7, border: `1px solid ${colors.borderCard}`, background: colors.bgCardHover, color: colors.textPrimary, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                Ver mais ({sortedOrders.length - visibleCount} restantes)
              </button>
            </div>
          )}
          </>
        )}
      </div>
    </div>
  );
}
